import { allSettled } from 'common/PromiseUtils';
import { identity } from 'lodash';
import promiseLimit from 'promise-limit';
import { useCallback } from 'react';
import {
  FileUploadInfoFragment,
  SignedUrlInput,
  SignedUrlInputOperation,
  useGetSignedUrlMutation,
} from 'src/gqlReactTypings.generated.d';

type Preprocessor = (file: File) => File | Promise<File>;

type UseFileSubmitOptions = Pick<SignedUrlInput, 'bucket' | 'path'> & {
  preprocess?: Preprocessor;
};

export const useFileSubmitHook = ({ preprocess, bucket, path }: UseFileSubmitOptions) => {
  const [getSignedUrl] = useGetSignedUrlMutation();
  const submitFiles = useCallback(
    async (acceptedFiles: File[]) => {
      const limit = promiseLimit<FileUploadInfoFragment>(2);

      const preprocessor: Preprocessor = preprocess ?? identity;

      const results = await allSettled<FileUploadInfoFragment, Error>(
        acceptedFiles.map((acceptedFile) =>
          limit(async () => {
            const file = await preprocessor(acceptedFile);

            const urlResponse = await getSignedUrl({
              variables: {
                input: {
                  bucket,
                  path,
                  operation: SignedUrlInputOperation.Put,
                  fileName: file.name,
                  mimeType: file.type,
                },
              },
            });

            if (urlResponse.errors?.length) {
              throw new Error(urlResponse.errors[0].message);
            }

            if (!urlResponse.data) {
              throw new Error('No data in response');
            }

            const uploadResponse = await fetch(urlResponse.data.getSignedUrl.signedUrl, {
              method: 'PUT',
              body: file,
            });

            if (!uploadResponse.ok) {
              const response = await uploadResponse.text();
              throw new Error(response);
            }

            return {
              ...urlResponse.data.getSignedUrl,
            };
          })
        )
      );

      return results.map((response) => {
        if (response.status === 'rejected') {
          console.error(response.reason.message);
          return {
            error: response.reason.message,
          };
        }
        return response.value;
      });
    },
    [bucket, getSignedUrl, path, preprocess]
  );

  return { submitFiles };
};
